import type {
  BonusType,
  BonusVertical,
  Currency,
  Language,
  PaymentProvider,
} from "@finbackoffice/enums";
import type { ICollection, ICollectionQuery } from "@finbackoffice/http-client";
import { HTTPClient } from "@finbackoffice/http-client";
import type {
  ICryptoAddressValidationResponse,
  IAddUserPaymentAccountRequest,
  IBankResponse,
  IBannerResponse,
  IBonusPaymentResponse,
  IBonusResponse,
  ICalculateMaxTicketAmountRequest,
  ICasinoBet,
  ICasinoEnum,
  ICasinoFavoriteRequest,
  ICasinoFavoriteResponse,
  ICasinoFunSessionRequest,
  ICasinoGame,
  ICasinoSessionRequest,
  ICasinoSessionResponse,
  ICasinoWinningBetRequest,
  ICasinoWinningBetsResponse,
  ICategorySummaryRequest,
  ICategorySummaryResponse,
  IChangePasswordRequest,
  IChatResponse,
  IChatStatsResponse,
  ICompleteRecoveryRequest,
  ICreateChatRequest,
  ICreateDocumentRequest,
  ICreateFileRequest,
  ICreateReferralRequest,
  ICreateReferredUserRequest,
  ICreateTicketRequest,
  ICreateWithdrawalRequest,
  ICryptoDetailsResponse,
  IDeleteChatBulkRequest,
  IDepositRemainderResponse,
  IDepositResponse,
  IDocumentResponse,
  IExternalUserResponse,
  IFileResponse,
  IFindActiveBannersRequest,
  IFindActiveNotificationsRequest,
  IFindActivePopupsRequest,
  IFindActiveReelsRequest,
  IFreeRoundUserResponse,
  IGetCasinoGamesRequest,
  IGetUserInterfaceRequest,
  IInitiateDepositRequest,
  IInitiateDepositResponse,
  IInitiateRecoveryRequest,
  ILoginRequest,
  ILoginResponse,
  IMaxTicketAmountResponse,
  IMessageCollection,
  IMessageResponse,
  INotificationResponse,
  IOtpResponse,
  IPaymentsPeriodConfigRequest,
  IPaymentsPeriodConfigResponse,
  IPaymentTransactionResponse,
  IPopupResponse,
  IPostMessageRequest,
  IProfileResponse,
  IQueryChatRequest,
  IQueryTransactionsRequest,
  IRecoveryResponse,
  IReelResponse,
  IReferralPersonalReport,
  IReferralResponse,
  IReferredUserResponse,
  IRegisterFromCPFNumberRequest,
  IRegisterFromCPFUserRequest,
  IRegisterRequest,
  ISessionResponse,
  ISiteConfigs,
  ITicketQuery,
  ITicketResponse,
  ITokenResponse,
  ITransactionResponse,
  IUpdateBonusAgentConfigRequest,
  IUpdateChatBulkRequest,
  IUpdateChatRequest,
  IUpdateDepositRequest,
  IUpdateMessageRequest,
  IUpdateProfileRequest,
  IUserInterfaceMapResponse,
  IUserPaymentAccountResponse,
  IUsersRegistrationConfigResponse,
  IValidateOtpRequest,
  IValidateRecoveryTokenRequest,
  IWinningTicketResponse,
  IWithdrawalRemainderResponse,
  IWithdrawalResponse,
  IBonusAgentConfig,
  IUpdateWithdrawalRequest,
  ILogoutResponse,
  IDeleteCasinoFavoriteResponse,
  IEventFeedExpandedResponse,
} from "./interface";

export class ClientBFFClient extends HTTPClient {
  private headers: Record<string, string> = {};

  setToken(token: string): void {
    if (typeof window === "undefined") {
      this.headers.Cookie = `jwt=${token};`;
    }
  }

  setHeaders(headers: Record<string, string>): void {
    this.headers = {
      ...this.headers,
      ...headers,
    };
  }

  async register(request: IRegisterRequest): Promise<ISessionResponse> {
    const session = await this.send<ISessionResponse>({
      method: "POST",
      path: "registration",
      headers: this.headers,
      body: request,
    });
    if (typeof session.token === "string") {
      this.setToken(session.token);
    }
    return session;
  }

  validatePhone(): Promise<IOtpResponse> {
    return this.send({
      method: "POST",
      path: "registration/validate-phone",
      headers: this.headers,
    });
  }

  validateEmail(): Promise<IOtpResponse> {
    return this.send({
      method: "POST",
      path: "registration/validate-email",
      headers: this.headers,
    });
  }

  async checkPhone(request: IValidateOtpRequest): Promise<ISessionResponse> {
    const session = await this.send<ISessionResponse>({
      method: "POST",
      path: "registration/validate-phone/check",
      headers: this.headers,
      body: request,
    });
    if (typeof session.token === "string") {
      this.setToken(session.token);
    }
    return session;
  }

  async checkEmail(request: IValidateOtpRequest): Promise<ISessionResponse> {
    const session = await this.send<ISessionResponse>({
      method: "POST",
      path: "registration/validate-email/check",
      headers: this.headers,
      body: request,
    });
    if (typeof session.token === "string") {
      this.setToken(session.token);
    }
    return session;
  }

  profile(): Promise<IProfileResponse> {
    return this.send({
      method: "GET",
      path: "profile",
      headers: this.headers,
    });
  }

  updateProfile(request: IUpdateProfileRequest): Promise<IProfileResponse> {
    return this.send({
      method: "PUT",
      path: "profile",
      headers: this.headers,
      body: request,
    });
  }

  async login(request: ILoginRequest): Promise<ILoginResponse> {
    const session = await this.send<ILoginResponse>({
      method: "POST",
      path: "login",
      headers: this.headers,
      body: request,
    });
    if (typeof session.token === "string") {
      this.setToken(session.token);
    }
    return session;
  }

  async logout(): Promise<ILogoutResponse> {
    const reponse = await this.send<ILogoutResponse>({
      method: "POST",
      path: "logout",
      headers: this.headers,
    });
    this.setToken("");
    return reponse;
  }

  loginOtp(request: IValidateOtpRequest): Promise<ILoginResponse> {
    return this.send({
      method: "POST",
      path: "login-otp",
      headers: this.headers,
      body: request,
    });
  }

  banks(): Promise<ICollection<IBankResponse>> {
    return this.send({
      method: "GET",
      path: "payment/bank",
      headers: this.headers,
    });
  }

  cryptoDetails(): Promise<ICollection<ICryptoDetailsResponse>> {
    return this.send({
      method: "GET",
      path: "payment/crypto-details",
      headers: this.headers,
    });
  }

  queryDeposits(
    request: ICollectionQuery<IDepositResponse>,
  ): Promise<ICollection<IDepositResponse>> {
    return this.send({
      method: "PUT",
      path: "payment/deposit",
      headers: this.headers,
      body: request,
    });
  }

  initiateDeposit(
    request: IInitiateDepositRequest,
  ): Promise<IInitiateDepositResponse> {
    return this.send({
      method: "POST",
      path: "payment/deposit",
      headers: this.headers,
      body: request,
    });
  }

  updateDeposit(
    id: number,
    request: IUpdateDepositRequest,
  ): Promise<IDepositResponse> {
    return this.send({
      method: "PUT",
      path: "payment/deposit/:id",
      headers: this.headers,
      params: {
        id: id.toString(),
      },
      body: request,
    });
  }

  updateWithdrawal(
    id: number,
    request: IUpdateWithdrawalRequest,
  ): Promise<IWithdrawalResponse> {
    return this.send({
      method: "PUT",
      path: "payment/withdrawal/:id",
      headers: this.headers,
      params: {
        id: id.toString(),
      },
      body: request,
    });
  }

  queryWithdrawals(
    request: ICollectionQuery<IWithdrawalResponse>,
  ): Promise<ICollection<IWithdrawalResponse>> {
    return this.send({
      method: "PUT",
      path: "payment/withdrawal",
      headers: this.headers,
      body: request,
    });
  }

  createWithdrawal(
    request: ICreateWithdrawalRequest,
  ): Promise<IWithdrawalResponse> {
    return this.send({
      method: "POST",
      path: "payment/withdrawal",
      headers: this.headers,
      body: request,
    });
  }

  get24HoursWithdrawalRemainder(
    currency: Currency,
  ): Promise<IWithdrawalRemainderResponse> {
    return this.send({
      method: "GET",
      path: "payment/withdrawal/24-hours/:currency",
      headers: this.headers,
      params: {
        currency: currency,
      },
    });
  }

  get24HoursDepositRemainder(
    currency: Currency,
  ): Promise<IDepositRemainderResponse> {
    return this.send({
      method: "GET",
      path: "payment/deposit/24-hours/:currency",
      headers: this.headers,
      params: {
        currency: currency,
      },
    });
  }

  queryPaymentsTransactions(
    request: ICollectionQuery<IPaymentTransactionResponse>,
  ): Promise<ICollection<IPaymentTransactionResponse>> {
    return this.send({
      method: "PUT",
      path: "payment/transaction",
      headers: this.headers,
      body: request,
    });
  }

  initiatePhoneVerification(): Promise<IOtpResponse> {
    return this.send({
      method: "POST",
      path: "payment/initiate-phone-verification",
      headers: this.headers,
    });
  }

  initiateEmailVerification(): Promise<IOtpResponse> {
    return this.send({
      method: "POST",
      path: "payment/initiate-email-verification",
      headers: this.headers,
    });
  }

  addUserPaymentAccount(
    request: IAddUserPaymentAccountRequest,
  ): Promise<IUserPaymentAccountResponse> {
    return this.send({
      method: "POST",
      path: "payment/user-account/address",
      headers: this.headers,
      body: request,
    });
  }

  getUserPaymentAccount(
    provider: PaymentProvider,
    currency: Currency,
  ): Promise<IUserPaymentAccountResponse> {
    return this.send({
      method: "GET",
      path: "payment/user-account/address/:provider/:currency",
      headers: this.headers,
      params: {
        provider: provider.toString(),
        currency: currency.toString(),
      },
    });
  }

  createReferral(request: ICreateReferralRequest): Promise<IReferralResponse> {
    return this.send({
      method: "POST",
      path: "referral",
      headers: this.headers,
      body: request,
    });
  }

  findReferral(): Promise<IReferralResponse> {
    return this.send({
      method: "GET",
      path: "referral",
      headers: this.headers,
    });
  }

  createReferredUser(
    request: ICreateReferredUserRequest,
  ): Promise<IReferredUserResponse> {
    return this.send({
      method: "POST",
      path: "referral/user",
      headers: this.headers,
      body: request,
    });
  }

  queryReferredUser(
    request: ICollectionQuery<IReferredUserResponse>,
  ): Promise<ICollection<IReferredUserResponse>> {
    return this.send({
      method: "PUT",
      path: "referral/user",
      headers: this.headers,
      body: request,
    });
  }

  getReferralPersonalReport(bonusId: number): Promise<IReferralPersonalReport> {
    return this.send({
      method: "GET",
      path: "referral/report/:bonusId",
      headers: this.headers,
      params: {
        bonusId: bonusId.toString(),
      },
    });
  }

  getBonuses(
    type: BonusType,
    count: number,
    vertical?: BonusVertical,
  ): Promise<ICollection<IBonusResponse>> {
    return this.send({
      method: "GET",
      path: "bonus/:type/:count",
      headers: this.headers,
      params: {
        type: type,
        count: count.toString(),
      },
      query: {
        vertical: vertical,
      },
    });
  }

  queryBonuses(
    request: ICollectionQuery<IBonusResponse>,
  ): Promise<ICollection<IBonusResponse>> {
    return this.send({
      method: "PUT",
      path: "bonus",
      headers: this.headers,
      body: request,
    });
  }

  payBonus(bonusId: number): Promise<ICollection<IBonusPaymentResponse>> {
    return this.send({
      method: "POST",
      path: "bonus/:bonusId/pay",
      headers: this.headers,
      params: {
        bonusId: bonusId.toString(),
      },
    });
  }

  acceptBonus(bonusId: number): Promise<IBonusResponse> {
    return this.send<IBonusResponse>({
      method: "POST",
      path: "bonus/:bonusId/accept",
      headers: this.headers,
      params: {
        bonusId: bonusId.toString(),
      },
    });
  }

  queryBonusPayment(
    request: ICollectionQuery<IBonusPaymentResponse>,
  ): Promise<ICollection<IBonusPaymentResponse>> {
    return this.send({
      method: "POST",
      path: "bonus/payment",
      headers: this.headers,
      body: request,
    });
  }

  getSiteConfig(): Promise<ISiteConfigs> {
    return this.send({
      method: "GET",
      path: "config/site",
      headers: this.headers,
    });
  }

  getAgentConfig(): Promise<IBonusAgentConfig> {
    return this.send({
      method: "GET",
      path: "config/agent",
      headers: this.headers,
    });
  }

  updateAgentConfig(
    request: IUpdateBonusAgentConfigRequest,
  ): Promise<IBonusAgentConfig> {
    return this.send({
      method: "PUT",
      path: "config/agent",
      headers: this.headers,
      body: request,
    });
  }

  getPaymentsPeriodConfig(): Promise<IPaymentsPeriodConfigResponse> {
    return this.send({
      method: "GET",
      path: "config/paymentsPeriod",
      headers: this.headers,
    });
  }

  updatePaymentsPeriodConfig(
    request: IPaymentsPeriodConfigRequest,
  ): Promise<IPaymentsPeriodConfigResponse> {
    return this.send({
      method: "PUT",
      path: "config/paymentsPeriod",
      headers: this.headers,
      body: request,
    });
  }

  getRegistrationConfig(): Promise<IUsersRegistrationConfigResponse> {
    return this.send({
      method: "GET",
      path: "config/registration",
      headers: this.headers,
    });
  }

  createChat(request: ICreateChatRequest): Promise<IChatResponse> {
    return this.send({
      method: "POST",
      path: "chat",
      headers: this.headers,
      body: request,
    });
  }

  updateChat(id: number, request: IUpdateChatRequest): Promise<IChatResponse> {
    return this.send({
      method: "PUT",
      path: "chat/:id",
      headers: this.headers,
      params: {
        id: id.toString(),
      },
      body: request,
    });
  }

  updateChatBulk(
    request: IUpdateChatBulkRequest[],
  ): Promise<ICollection<IChatResponse>> {
    return this.send({
      method: "PUT",
      path: "chat/bulk",
      headers: this.headers,
      body: request,
    });
  }

  findChat(id: number): Promise<IChatResponse> {
    return this.send({
      method: "GET",
      path: "chat/:id",
      headers: this.headers,
      params: {
        id: id.toString(),
      },
    });
  }

  deleteChat(id: number): Promise<IChatResponse> {
    return this.send({
      method: "DELETE",
      path: "chat/:id",
      headers: this.headers,
      params: {
        id: id.toString(),
      },
    });
  }

  deleteChatBulk(request: IDeleteChatBulkRequest[]): Promise<IChatResponse> {
    return this.send({
      method: "DELETE",
      path: "chat/bulk",
      headers: this.headers,
      body: request,
    });
  }

  getChatStats(): Promise<IChatStatsResponse> {
    return this.send({
      method: "GET",
      path: "chat/stats",
      headers: this.headers,
    });
  }

  createMessage(
    chatId: number,
    request: IPostMessageRequest,
  ): Promise<IChatResponse> {
    return this.send({
      method: "POST",
      path: "chat/:chatId/message",
      headers: this.headers,
      params: {
        chatId: chatId.toString(),
      },
      body: request,
    });
  }

  updateMessage(
    chatId: number,
    messageId: number,
    request: IUpdateMessageRequest,
  ): Promise<IChatResponse> {
    return this.send({
      method: "PUT",
      path: "chat/:chatId/message/:messageId",
      headers: this.headers,
      params: {
        chatId: chatId.toString(),
        messageId: messageId.toString(),
      },
      body: request,
    });
  }

  queryChat(request: IQueryChatRequest): Promise<ICollection<IChatResponse>> {
    return this.send({
      method: "PUT",
      path: "chat",
      headers: this.headers,
      body: request,
    });
  }

  queryMessage(chatId: number): Promise<IMessageCollection> {
    return this.send({
      method: "GET",
      path: "chat/:id/message",
      headers: this.headers,
      params: {
        id: chatId.toString(),
      },
    });
  }

  searchMessage(query: string): Promise<ICollection<IMessageResponse>> {
    return this.send({
      method: "GET",
      path: "chat/message/search",
      headers: this.headers,
      query: {
        query: query,
      },
    });
  }

  queryTransaction(
    request: IQueryTransactionsRequest,
  ): Promise<ICollection<ITransactionResponse>> {
    return this.send({
      method: "PUT",
      path: "transaction",
      headers: this.headers,
      body: request,
    });
  }

  queryCasinoGames(
    request: ICollectionQuery<ICasinoGame>,
  ): Promise<ICollection<ICasinoGame>> {
    return this.send({
      method: "PUT",
      path: "casino/game",
      headers: this.headers,
      body: request,
    });
  }

  getCasinoGames(
    request: IGetCasinoGamesRequest,
  ): Promise<ICollection<ICasinoGame>> {
    return this.send({
      method: "GET",
      path: "casino/game",
      query: {
        page: request.page.toString(),
        items_per_page: request.items_per_page.toString(),
        id: request.id,
        mobile: request.mobile.toString(),
        megaways: request.megaways?.toString(),
        has_jackpot: request.has_jackpot?.toString(),
        bonusbuy: request.bonusbuy?.toString(),
        types: request.types?.length ? request.types : undefined,
        name: request.name,
        category: request.category,
        tag: request.tag,
      },
    });
  }

  getCasinoGameSummaryByCategory(
    request: ICategorySummaryRequest,
  ): Promise<ICategorySummaryResponse[]> {
    return this.send({
      method: "GET",
      path: "casino/game/summary-by-category",
      query: {
        mobile: request.mobile.toString(),
        megaways: request.megaways?.toString(),
        has_jackpot: request.has_jackpot?.toString(),
        bonusbuy: request.bonusbuy?.toString(),
        types: request.types.length ? request.types : undefined,
        tag: request.tag,
      },
    });
  }

  getCasinoEnums(): Promise<ICollection<ICasinoEnum>> {
    return this.send({
      method: "GET",
      path: "casino/enums",
      headers: this.headers,
    });
  }

  createCasinoRealSession(
    request: ICasinoSessionRequest,
  ): Promise<ICasinoSessionResponse> {
    return this.send({
      method: "POST",
      path: "casino/session/real",
      headers: this.headers,
      body: request,
    });
  }

  createCasinoFunSession(
    request: ICasinoFunSessionRequest,
  ): Promise<ICasinoSessionResponse> {
    return this.send({
      method: "POST",
      path: "casino/session/fun",
      headers: this.headers,
      body: request,
    });
  }

  queryCasinoBets(
    request: ICollectionQuery<ICasinoBet>,
  ): Promise<ICollection<ICasinoBet>> {
    return this.send({
      method: "PUT",
      path: "casino/bet",
      headers: this.headers,
      body: request,
    });
  }

  createCasinoFavorite(
    request: ICasinoFavoriteRequest,
  ): Promise<ICasinoFavoriteResponse> {
    return this.send({
      method: "POST",
      path: "casino/favorite",
      headers: this.headers,
      body: request,
    });
  }

  findCasinoFavoriteByUserId(
    mobile: boolean,
  ): Promise<ICollection<ICasinoFavoriteResponse>> {
    return this.send({
      method: "GET",
      path: "casino/favorite",
      headers: this.headers,
      query: {
        mobile: mobile.toString(),
      },
    });
  }

  deleteCasinoFavorite(gameId: string): Promise<IDeleteCasinoFavoriteResponse> {
    return this.send<IDeleteCasinoFavoriteResponse>({
      method: "DELETE",
      path: "casino/favorite/:gameId",
      headers: this.headers,
      params: {
        gameId: gameId,
      },
    });
  }

  queryTickets(request: ITicketQuery): Promise<ICollection<ITicketResponse>> {
    return this.send({
      method: "PUT",
      path: "ticket",
      headers: this.headers,
      body: request,
    });
  }

  createTicket(request: ICreateTicketRequest): Promise<ITicketResponse> {
    return this.send({
      method: "POST",
      path: "ticket",
      headers: this.headers,
      body: request,
    });
  }

  calculateMaxBet(
    request: ICalculateMaxTicketAmountRequest,
  ): Promise<IMaxTicketAmountResponse> {
    return this.send({
      method: "PUT",
      path: "ticket/max",
      headers: this.headers,
      body: request,
    });
  }

  getWinningTickets(): Promise<IWinningTicketResponse[]> {
    return this.send({
      method: "GET",
      path: "ticket/winning-tickets",
      headers: this.headers,
    });
  }

  validateCryptoAddress(
    provider: PaymentProvider,
    currency: Currency,
    address: string,
  ): Promise<ICryptoAddressValidationResponse> {
    return this.send({
      method: "GET",
      path: "crypto/validate/:provider/:currency/:address",
      headers: this.headers,
      params: {
        provider: provider.toString(),
        currency: currency.toString(),
        address: address,
      },
    });
  }

  getPublicNotifications(
    request: IFindActiveNotificationsRequest,
  ): Promise<ICollection<INotificationResponse>> {
    return this.send({
      method: "GET",
      path: "notification/public/:language",
      headers: this.headers,
      params: {
        language: request.language.toString(),
      },
      query: {
        tags: request.tags.length ? request.tags : undefined,
      },
    });
  }

  getLoggedInNotifications(
    request: IFindActiveNotificationsRequest,
  ): Promise<ICollection<INotificationResponse>> {
    return this.send({
      method: "GET",
      path: "notification/loggedIn/:language",
      headers: this.headers,
      params: {
        language: request.language.toString(),
      },
      query: {
        tags: request.tags.length ? request.tags : undefined,
      },
    });
  }

  getPublicPopups(
    request: IFindActivePopupsRequest,
  ): Promise<ICollection<IPopupResponse>> {
    return this.send({
      method: "GET",
      path: "popup/public/:language",
      headers: this.headers,
      params: {
        language: request.language.toString(),
      },
      query: {
        tags: request.tags.length ? request.tags : undefined,
      },
    });
  }

  getLoggedInPopups(
    request: IFindActivePopupsRequest,
  ): Promise<ICollection<IPopupResponse>> {
    return this.send({
      method: "GET",
      path: "popup/loggedIn/:language",
      headers: this.headers,
      params: {
        language: request.language.toString(),
      },
      query: {
        tags: request.tags.length ? request.tags : undefined,
      },
    });
  }

  createFile(request: ICreateFileRequest): Promise<IFileResponse> {
    const body = new FormData();
    body.append("path", request.path);
    body.append("file", request.body, request.filename);
    return this.send({
      method: "POST",
      path: "document/file",
      headers: this.headers,
      body: body,
    });
  }

  createDocument(request: ICreateDocumentRequest): Promise<IDocumentResponse> {
    return this.send({
      method: "POST",
      path: "document",
      headers: this.headers,
      body: request,
    });
  }

  getUserDocuments(): Promise<ICollection<IDocumentResponse>> {
    return this.send({
      method: "GET",
      path: "document",
      headers: this.headers,
    });
  }

  getBanners(
    request: IFindActiveBannersRequest,
  ): Promise<ICollection<IBannerResponse>> {
    return this.send({
      method: "GET",
      path: "banner/:language",
      headers: this.headers,
      params: {
        language: request.language.toString(),
      },
      query: {
        tags: request.tags.length > 0 ? request.tags : undefined,
        provider: request.provider,
        equal: request.equal?.toString(),
      },
    });
  }

  getReels(
    request: IFindActiveReelsRequest,
  ): Promise<ICollection<IReelResponse>> {
    return this.send<ICollection<IReelResponse>>({
      method: "GET",
      path: "reel/:language",
      headers: this.headers,
      params: {
        language: request.language.toString(),
      },
      query: {
        tags: request.tags.length > 0 ? request.tags : undefined,
        provider: request.provider,
        equal: request.equal?.toString(),
      },
    });
  }

  getReel(language: Language, reel_id: number): Promise<IReelResponse> {
    return this.send<IReelResponse>({
      method: "GET",
      path: "reel/:language/:id",
      headers: this.headers,
      params: {
        language: language.toString(),
        id: reel_id.toString(),
      },
    });
  }

  addReelLike(reel_id: number): Promise<IReelResponse> {
    return this.send({
      method: "POST",
      path: "reel/:id/like",
      headers: this.headers,
      params: {
        id: reel_id.toString(),
      },
    });
  }

  initiateRecovery(
    request: IInitiateRecoveryRequest,
  ): Promise<IRecoveryResponse> {
    return this.send({
      method: "POST",
      path: "recovery/initiate",
      headers: this.headers,
      body: request,
    });
  }

  validateRecoveryToken(
    request: IValidateRecoveryTokenRequest,
  ): Promise<ITokenResponse> {
    return this.send({
      method: "POST",
      path: "recovery/validate",
      headers: this.headers,
      body: request,
    });
  }

  async completeRecovery(
    request: ICompleteRecoveryRequest,
  ): Promise<ISessionResponse> {
    const session = await this.send<ISessionResponse>({
      method: "POST",
      path: "recovery/complete",
      headers: this.headers,
      body: request,
    });
    if (typeof session.token === "string") {
      this.setToken(session.token);
    }
    return session;
  }

  changePassword(request: IChangePasswordRequest): Promise<ISessionResponse> {
    return this.send<ISessionResponse>({
      method: "POST",
      path: "profile/change-password",
      headers: this.headers,
      body: request,
    });
  }

  getCasinoWinningBets(
    request?: ICasinoWinningBetRequest,
  ): Promise<ICasinoWinningBetsResponse> {
    return this.send({
      method: "GET",
      path: "casino-stats/bets",
      headers: this.headers,
      query: {
        game_id: request?.game_id ?? "",
      },
    });
  }

  getDesktopTranslations(
    request: IGetUserInterfaceRequest,
  ): Promise<IUserInterfaceMapResponse> {
    return this.send({
      method: "GET",
      path: "user-interface/desktop/:language",
      headers: this.headers,
      params: {
        language: request.language.toString(),
      },
      query: {
        scopes: request.scopes,
      },
    });
  }

  getMobileTranslations(
    request: IGetUserInterfaceRequest,
  ): Promise<IUserInterfaceMapResponse> {
    return this.send({
      method: "GET",
      path: "user-interface/mobile/:language",
      headers: this.headers,
      params: {
        language: request.language.toString(),
      },
      query: {
        scopes: request.scopes,
      },
    });
  }

  getExternalUserByCPFNumber(
    cpf_number: string,
  ): Promise<IExternalUserResponse> {
    return this.send({
      method: "GET",
      path: "registration/external/:cpf_number",
      headers: this.headers,
      params: {
        cpf_number,
      },
    });
  }

  async registerFromCPFUser(
    request: IRegisterFromCPFUserRequest,
  ): Promise<ISessionResponse> {
    const session = await this.send<ISessionResponse>({
      method: "POST",
      path: "registration/cpf/without-verification",
      headers: this.headers,
      body: request,
    });
    if (typeof session.token === "string") {
      this.setToken(session.token);
    }
    return session;
  }

  async registerFromCPFNumber(
    request: IRegisterFromCPFNumberRequest,
  ): Promise<ISessionResponse> {
    const session = await this.send<ISessionResponse>({
      method: "POST",
      path: "registration/cpf/with-verification",
      headers: this.headers,
      body: request,
    });
    if (typeof session.token === "string") {
      this.setToken(session.token);
    }
    return session;
  }

  queryFreeRoundUser(
    request: ICollectionQuery<IFreeRoundUserResponse>,
  ): Promise<ICollection<IFreeRoundUserResponse>> {
    return this.send({
      method: "PUT",
      path: "casino/free-round-user",
      headers: this.headers,
      body: request,
    });
  }

  searchEvent(
    eventId: string,
    language: Language,
  ): Promise<IEventFeedExpandedResponse[]> {
    return this.send({
      method: "GET",
      path: "feed/event/search/:id",
      headers: this.headers,
      params: {
        id: eventId,
      },
      query: {
        language: language,
      },
    });
  }
}
